import React, { useRef, useEffect, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useSearchParams } from 'react-router-dom';
import { Plus, Minus } from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CTA from '../components/home/CTA';

gsap.registerPlugin(ScrollTrigger);

// All advisory board members data - Qasaba Energy team
const advisoryMembers = [
  {
    id: 1,
    name: 'Douglas Quezada',
    title: 'Chairman',
    bio: `Douglas Quezada serves as Chairman of Qasaba Energy, bringing decades of leadership experience in energy innovation and strategic business development. His vision and guidance have been instrumental in positioning Qasaba at the forefront of clean energy transformation.

As Chairman, Douglas oversees the company's strategic direction and ensures alignment between the board, management, and shareholders. His expertise spans across international business development, government relations, and technology commercialization.

Douglas is committed to advancing Qasaba's mission of delivering zero-emission energy solutions while creating sustainable value for stakeholders and communities worldwide.`,
    image: '/images/douglas-quezada.jpeg'
  },
  {
    id: 2,
    name: 'Mohammed Alexander',
    title: 'Vice Chairman',
    bio: `Mohammed Alexander serves as Vice Chairman of Qasaba Energy, playing a crucial role in shaping the company's strategic initiatives and corporate governance.

With extensive experience in the energy sector and international business, Mohammed brings a unique perspective that bridges traditional energy markets with innovative clean technology solutions. His leadership ensures that Qasaba maintains its commitment to excellence while pursuing aggressive growth objectives.

Mohammed works closely with the Chairman and Board to drive key initiatives that advance Qasaba's position as a leader in sustainable energy development.`,
    image: '/images/mohammed-alexander.jpeg'
  },
  {
    id: 3,
    name: 'Richard Haws',
    title: 'Managing Director',
    bio: `Richard Haws serves as Managing Director of Qasaba Energy, responsible for the day-to-day operations and execution of the company's strategic vision.

With a strong background in energy technology and operational excellence, Richard leads cross-functional teams to deliver on Qasaba's ambitious goals. His hands-on approach and deep understanding of both technical and commercial aspects of the energy industry have been key to the company's operational success.

Richard ensures that Qasaba's innovative technologies are deployed efficiently and effectively, maximizing value creation while maintaining the highest standards of safety and environmental responsibility.`,
    image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=600&h=800&fit=crop&crop=top'
  },
  {
    id: 4,
    name: 'Naru',
    title: 'Chief Financial Officer (CFO)',
    bio: `Naru serves as Chief Financial Officer of Qasaba Energy, overseeing all financial operations, strategic planning, and investor relations.

With expertise in corporate finance, capital markets, and financial strategy, Naru ensures that Qasaba maintains a strong financial foundation while pursuing strategic growth opportunities. His leadership in financial planning and analysis supports the company's ambitious expansion goals.

Naru's commitment to financial transparency and disciplined capital allocation has been instrumental in building investor confidence and securing the resources needed to advance Qasaba's mission.`,
    image: 'https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=600&h=800&fit=crop&crop=top'
  },
  {
    id: 5,
    name: 'Richard Lee',
    title: 'Board Member',
    bio: `Richard Lee serves as a Board Member of Qasaba Energy, contributing valuable expertise in corporate governance and strategic oversight.

With extensive experience across multiple industries and leadership roles, Richard provides critical guidance on strategic decisions, risk management, and corporate development. His insights help ensure that Qasaba maintains the highest standards of corporate governance while pursuing its growth objectives.

Richard is committed to supporting Qasaba's mission of transforming the energy landscape through innovative, sustainable solutions.`,
    image: '/images/richard-lee.jpeg'
  },
  {
    id: 8,
    name: 'Usama Aziz',
    title: 'COO and Chief Legal Advisor',
    bio: `Usama Aziz serves as Chief Operating Officer and Chief Legal Advisor of Qasaba Energy, a dual role that combines operational leadership with legal expertise.

As COO, Usama oversees the company's operational functions, ensuring efficient execution across all business units. As Chief Legal Advisor, he provides strategic legal guidance on corporate matters, regulatory compliance, and international transactions.

Usama's unique combination of operational and legal expertise enables Qasaba to navigate complex business environments while maintaining regulatory compliance and mitigating risk.`,
    image: '/images/usama-aziz.jpeg'
  },
  {
    id: 9,
    name: 'Ali Sultan Al Hajri',
    title: 'MENA Senior Financial Advisor',
    bio: `Ali Sultan Al Hajri serves as MENA Senior Financial Advisor for Qasaba Energy, providing strategic financial guidance for the company's Middle East and North Africa operations.

With deep expertise in regional financial markets, sovereign wealth funds, and energy sector investments, Ali plays a crucial role in developing and executing Qasaba's MENA strategy. His relationships and understanding of the region's unique business environment are invaluable assets.

Ali is committed to supporting Qasaba's growth in the MENA region while ensuring alignment with local stakeholder interests and regulatory requirements.`,
    image: '/images/ali-al-hajri.jpeg'
  },
  {
    id: 10,
    name: 'Ben Moufid Dekhil',
    title: 'Vice President, Business Development',
    bio: `Ben Moufid Dekhil serves as Vice President of Business Development at Qasaba Energy, leading efforts to expand the company's market presence and strategic partnerships.

With extensive experience in energy sector business development and international markets, Ben identifies and pursues growth opportunities that align with Qasaba's strategic objectives. His work spans partnership development, market entry strategies, and commercial negotiations.

Ben's entrepreneurial mindset and deep industry relationships are instrumental in driving Qasaba's business growth and establishing the company as a leader in clean energy solutions.`,
    image: '/images/ben-moufid-dekhil.jpeg'
  }
];

const Advisory = () => {
  const heroRef = useRef(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [expandedId, setExpandedId] = useState(null);

  // Check URL params for open member
  useEffect(() => {
    const openId = searchParams.get('open');
    if (openId) {
      setExpandedId(parseInt(openId));
      // Scroll to the member after a short delay
      setTimeout(() => {
        const element = document.getElementById(`member-${openId}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 500);
    }
  }, [searchParams]);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        '.advisory-title',
        { opacity: 0, x: 50 },
        { opacity: 1, x: 0, duration: 1, ease: 'power3.out', delay: 0.3 }
      );
      gsap.fromTo(
        '.advisory-subtitle',
        { opacity: 0, y: 30 },
        { opacity: 1, y: 0, duration: 0.8, ease: 'power3.out', delay: 0.6 }
      );
    }, heroRef);

    return () => ctx.revert();
  }, []);

  const toggleMember = (id) => {
    if (expandedId === id) {
      setExpandedId(null);
      setSearchParams({});
    } else {
      setExpandedId(id);
      setSearchParams({ open: id });
    }
  };

  return (
    <div className="min-h-screen bg-[#0B1121]">
      <Header />
      <main>
        {/* Hero Section */}
        <section ref={heroRef} className="relative min-h-[70vh] flex flex-col">
          {/* Background Image */}
          <div className="absolute inset-0">
            <img
              src="https://images.unsplash.com/photo-1635070041078-e363dbe005cb?w=1920&q=80"
              alt=""
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0B1121]/90 via-[#0B1121]/60 to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0B1121] via-transparent to-[#0B1121]/30" />
          </div>

          {/* Content */}
          <div className="relative z-10 flex-1 flex flex-col justify-between pt-32 pb-16">
            {/* Title - Right aligned */}
            <div className="l-container">
              <h1 className="font-serif text-6xl lg:text-7xl xl:text-8xl text-white text-right advisory-title">
                Advisory Board.
              </h1>
            </div>

            {/* Subtitle - Left aligned at bottom */}
            <div className="l-container mt-auto">
              <p className="font-serif text-xl lg:text-2xl text-white/90 max-w-lg leading-relaxed advisory-subtitle">
                With an immense knowledge of the industry and a deep understanding of the science, we've come together to help Qasaba deliver net-zero energy now.
              </p>
            </div>
          </div>
        </section>

        {/* Team List - Accordion Style */}
        <section className="bg-white">
          <div className="max-w-[1400px] mx-auto">
            {advisoryMembers.map((member, index) => (
              <MemberAccordion
                key={member.id}
                member={member}
                index={index}
                isExpanded={expandedId === member.id}
                onToggle={() => toggleMember(member.id)}
              />
            ))}
          </div>
        </section>

        <CTA />
      </main>
      <Footer />
    </div>
  );
};

// Member Accordion Component
const MemberAccordion = ({ member, index, isExpanded, onToggle }) => {
  const contentRef = useRef(null);
  const rowRef = useRef(null);

  useEffect(() => {
    gsap.fromTo(
      rowRef.current,
      { opacity: 0, y: 20 },
      {
        opacity: 1,
        y: 0,
        duration: 0.5,
        delay: index * 0.05,
        scrollTrigger: {
          trigger: rowRef.current,
          start: 'top 90%',
        },
      }
    );
  }, [index]);

  return (
    <div
      id={`member-${member.id}`}
      ref={rowRef}
      className="border-b border-gray-200"
    >
      {/* Collapsed Row */}
      <div
        className="flex items-center gap-6 lg:gap-12 px-6 lg:px-16 py-6 lg:py-8 cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={onToggle}
      >
        {/* Small Photo */}
        <div className="w-14 h-18 lg:w-20 lg:h-24 flex-shrink-0 overflow-hidden rounded">
          <img
            src={member.image}
            alt={member.name}
            className="w-full h-full object-cover"
          />
        </div>

        {/* Name & Title */}
        <div className="flex-1">
          <h3 className="font-serif text-xl lg:text-2xl xl:text-3xl text-[#0B1121] mb-1">
            {member.name}
          </h3>
          <p className="text-[#0B1121]/60 text-sm lg:text-base">
            {member.title}
          </p>
          {member.subtitle && !isExpanded && (
            <p className="text-[#0B1121]/40 text-xs lg:text-sm mt-1">
              {member.subtitle}
            </p>
          )}
        </div>

        {/* Toggle Icon */}
        <div className="flex-shrink-0">
          {isExpanded ? (
            <Minus size={24} className="text-[#0B1121]" />
          ) : (
            <Plus size={24} className="text-[#0B1121]" />
          )}
        </div>
      </div>

      {/* Expanded Content */}
      <div
        ref={contentRef}
        className={`overflow-hidden transition-all duration-500 ease-in-out ${
          isExpanded ? 'max-h-[3000px] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="px-6 lg:px-16 pb-12">
          <div className="flex flex-col lg:flex-row gap-8 lg:gap-16">
            {/* Large Photo */}
            <div className="w-full lg:w-80 flex-shrink-0">
              <div className="aspect-[3/4] overflow-hidden rounded-lg">
                <img
                  src={member.image}
                  alt={member.name}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            {/* Bio Content */}
            <div className="flex-1 lg:pt-4">
              {member.subtitle && (
                <p className="text-[#4ECDC4] font-medium mb-6 text-sm lg:text-base">
                  {member.subtitle}
                </p>
              )}
              <div className="text-[#0B1121]/70 leading-relaxed space-y-4 text-sm lg:text-base">
                {member.bio.split('\n\n').map((paragraph, i) => (
                  <p key={i}>{paragraph}</p>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Advisory;
